import "@/App.css";
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Globe, 
  Building2, 
  TrendingUp, 
  Users, 
  Shield,
  Target,
  Scale,
  Lock,
  ChevronDown,
  ArrowRight,
  Handshake
} from 'lucide-react';

import Navigation from './components/Navigation';
import ScarabEmblem from './components/ScarabEmblem';
import ServiceCard from './components/ServiceCard';
import ContactForm from './components/ContactForm';

// Services data
const services = [
  {
    icon: Briefcase,
    title: "Strategic Project Management",
    description: "End-to-end oversight of complex initiatives, from inception through execution, ensuring alignment with strategic objectives."
  },
  {
    icon: Globe,
    title: "Cross-Border Structuring & Regulation",
    description: "Navigate multi-jurisdictional frameworks with precision. Regulatory compliance and strategic structuring across borders."
  },
  {
    icon: Building2,
    title: "Real Estate Development Advisory",
    description: "Comprehensive guidance on development projects, from site selection through completion. Risk mitigation and value optimization."
  },
  {
    icon: TrendingUp,
    title: "Investment Structuring",
    description: "Sophisticated capital structures designed for optimal returns and risk management. Tax-efficient deployment strategies."
  },
  {
    icon: Users,
    title: "Network, Partnerships & Sponsorships",
    description: "Leverage decades of cultivated relationships. Access to decision-makers, capital partners, and sponsorship opportunities. Connecting visionary projects with aligned investors and brand partners."
  },
  {
    icon: Shield,
    title: "Execution & Governance Oversight",
    description: "Institutional-grade oversight ensuring projects meet the highest standards of governance and execution excellence."
  }
];

// Approach data
const approaches = [
  {
    icon: Target,
    title: "Strategic Alignment",
    description: "Every engagement begins with deep understanding. We align our approach with your long-term vision and objectives."
  },
  {
    icon: Scale,
    title: "Regulatory Mastery",
    description: "Decades of experience navigating complex regulatory environments. We anticipate challenges before they arise."
  },
  {
    icon: Lock,
    title: "Execution Control",
    description: "Rigorous project governance and milestone management. We deliver what we promise, when we promise it."
  }
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function App() {
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-dark-primary min-h-screen" data-testid="app-container">
      <Navigation />

      {/* SECTION 1: Hero */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-bg-pattern" />
        <div className="geo-grid" />
        
        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="flex flex-col items-center"
          >
            <ScarabEmblem />
            
            <motion.h1
              className="font-heading text-5xl sm:text-6xl lg:text-8xl tracking-tight text-gold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              data-testid="hero-title"
            >
              KHEPRY
            </motion.h1>

            <motion.p
              className="font-heading text-xl sm:text-2xl lg:text-3xl text-text-primary mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              style={{ color: '#F2F2F2' }}
            >
              Transforming Vision into Enduring Structures
            </motion.p>

            <motion.p
              className="text-sm sm:text-base max-w-2xl mx-auto mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              style={{ color: '#A1A1AA' }}
            >
              Strategic project management and advisory built on decades of network, 
              regulatory expertise, and execution precision.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
            >
              <button 
                onClick={() => scrollToSection('#contact')}
                className="btn-primary"
                data-testid="hero-cta-consultation"
              >
                Request Consultation
              </button>
              <button 
                onClick={() => scrollToSection('#services')}
                className="btn-secondary"
                data-testid="hero-cta-expertise"
              >
                Explore Our Expertise
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
        >
          <span className="overline text-xs" style={{ color: '#8A7442' }}>Scroll</span>
          <div className="scroll-line" />
        </motion.div>
      </section>

      {/* SECTION 2: Our Foundation */}
      <section id="foundation" className="section bg-dark-secondary" data-testid="foundation-section">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.span className="overline" variants={fadeInUp}>Our Foundation</motion.span>
            
            <motion.h2 
              className="section-title mt-4"
              variants={fadeInUp}
              style={{ color: '#F2F2F2' }}
            >
              Built on Legacy.<br />
              <span className="text-gold">Designed for the Future.</span>
            </motion.h2>
            
            <div className="section-divider" />

            <motion.div className="space-y-6 mt-8" variants={fadeInUp}>
              <p style={{ color: '#A1A1AA', fontSize: '1.125rem', lineHeight: '1.8' }}>
                The scarab—revered across ancient civilizations—represents transformation, 
                protection, and the eternal cycle of renewal. It symbolizes the power to 
                navigate complexity and emerge stronger.
              </p>
              <p style={{ color: '#A1A1AA', fontSize: '1.125rem', lineHeight: '1.8' }}>
                Ancient architects understood what modern markets often forget: that enduring 
                structures require precise alignment, unwavering foundations, and the wisdom 
                to build for generations, not quarters.
              </p>
              <p style={{ color: '#A1A1AA', fontSize: '1.125rem', lineHeight: '1.8' }}>
                Khepry applies these timeless principles to contemporary capital deployment, 
                real estate development, and regulated market navigation. We build structures 
                designed to withstand the test of time.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: Core Services */}
      <section id="services" className="section" data-testid="services-section">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline">Core Services</span>
            <h2 className="section-title mt-4" style={{ color: '#F2F2F2' }}>
              Comprehensive <span className="text-gold">Expertise</span>
            </h2>
            <div className="section-divider mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard
                key={service.title}
                icon={service.icon}
                title={service.title}
                description={service.description}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Our Approach */}
      <section id="approach" className="section bg-dark-secondary" data-testid="approach-section">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline">Our Approach</span>
            <h2 className="section-title mt-4" style={{ color: '#F2F2F2' }}>
              Precision. Structure. <span className="text-gold">Discretion.</span>
            </h2>
            <div className="section-divider mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {approaches.map((approach, index) => (
              <motion.div
                key={approach.title}
                className="approach-column px-6 md:px-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                data-testid={`approach-column-${index}`}
              >
                <approach.icon 
                  className="text-gold mb-6" 
                  size={40} 
                  strokeWidth={1} 
                />
                <h3 className="font-heading text-xl mb-4" style={{ color: '#F2F2F2' }}>
                  {approach.title}
                </h3>
                <p style={{ color: '#A1A1AA', lineHeight: '1.8' }}>
                  {approach.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: Network & Scale */}
      <section id="network" className="section" data-testid="network-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline">Network & Scale</span>
              <h2 className="section-title mt-4" style={{ color: '#F2F2F2' }}>
                A Network Built<br />
                <span className="text-gold">Over Decades</span>
              </h2>
              <div className="section-divider" />
              
              <p className="mt-6" style={{ color: '#A1A1AA', fontSize: '1.125rem', lineHeight: '1.8' }}>
                True access cannot be manufactured overnight. Our relationships with 
                international partners, legal specialists, and institutional capital 
                sources have been cultivated through years of successful collaboration 
                and mutual trust.
              </p>
            </motion.div>

            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                { label: "International Partners", desc: "Cross-border expertise across major markets" },
                { label: "Legal & Regulatory Specialists", desc: "Deep bench of specialized advisors" },
                { label: "Institutional Capital Relationships", desc: "Access to sophisticated capital sources" },
                { label: "Real Estate Operators", desc: "Proven development and management partners" },
                { label: "Cross-Industry Expertise", desc: "Diverse sector knowledge and connections" }
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  className="flex items-start gap-4 p-4 bg-dark-card border border-subtle hover:border-gold transition-colors duration-300"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}
                  data-testid={`network-item-${index}`}
                >
                  <ArrowRight className="text-gold mt-1 flex-shrink-0" size={16} strokeWidth={1.5} />
                  <div>
                    <h4 className="font-heading text-lg" style={{ color: '#F2F2F2' }}>{item.label}</h4>
                    <p style={{ color: '#A1A1AA', fontSize: '0.875rem' }}>{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Why Khepry */}
      <section className="section bg-dark-secondary" data-testid="why-section">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline">Why Khepry</span>
            <h2 className="section-title mt-4 mb-8" style={{ color: '#F2F2F2' }}>
              Where <span className="text-gold">Execution</span> is Everything
            </h2>
            
            <div className="gold-glow absolute inset-0 -z-10" />
            
            <p className="text-lg md:text-xl leading-relaxed mb-8" style={{ color: '#A1A1AA' }}>
              In environments defined by regulation, capital intensity, and geopolitical 
              complexity, execution is everything. Theoretical expertise without 
              implementation capability is insufficient.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {[
                { label: "Structured Thinking", icon: Target },
                { label: "Risk Mitigation", icon: Shield },
                { label: "Long-term Durability", icon: Building2 },
                { label: "Confidentiality", icon: Lock }
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  className="p-6 bg-dark-card border border-subtle text-center"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}
                  data-testid={`why-item-${index}`}
                >
                  <item.icon className="text-gold mx-auto mb-4" size={32} strokeWidth={1} />
                  <span className="text-sm font-medium" style={{ color: '#F2F2F2' }}>{item.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 7: Contact */}
      <section id="contact" className="section" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline">Contact</span>
              <h2 className="section-title mt-4" style={{ color: '#F2F2F2' }}>
                Initiate the <span className="text-gold">Conversation</span>
              </h2>
              <div className="section-divider" />
              
              <p className="mt-6" style={{ color: '#A1A1AA', fontSize: '1.125rem', lineHeight: '1.8' }}>
                Every significant undertaking begins with a conversation. Share your 
                objectives, and we will determine how Khepry can contribute to your success.
              </p>
              
              <div className="mt-8 p-6 bg-dark-card border border-subtle" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                <p className="overline mb-2">Direct Inquiries</p>
                <a 
                  href="mailto:management@khepry.com"
                  className="text-gold gold-underline text-lg"
                  data-testid="contact-email-link"
                >
                  management@khepry.com
                </a>
              </div>
            </motion.div>

            <div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-heading text-2xl text-gold mb-2">KHEPRY</h3>
            <p style={{ color: '#A1A1AA', fontSize: '0.875rem' }}>
              Strategic Advisory & Project Structuring
            </p>
            
            <div className="footer-divider" />
            
            <p style={{ color: '#52525B', fontSize: '0.75rem', maxWidth: '600px', margin: '0 auto' }}>
              The information provided on this website is for general informational purposes only 
              and does not constitute legal, financial, or professional advice. All engagements 
              are subject to formal agreements and due diligence processes.
            </p>
            
            <p className="mt-6" style={{ color: '#52525B', fontSize: '0.75rem' }}>
              &copy; {new Date().getFullYear()} Khepry. All rights reserved. · <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" style={{ color: '#52525B', textDecoration: 'none' }}>Built with Emergent</a>
            </p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
}

export default App;
